/**
 * Value Stream Mapping (VSM) domain model — the FE↔BE contract for the VSM
 * module. Ported from the pbdesign behaviour oracle
 * (`src/wireframe/data/{vsms,vsmEdit,vsmMonitoring}.ts` +
 * `.../components/vsm/edit/phases.ts`). Seed fixtures live in the mock layer
 * (`@playbookai/vsm-feature/src/api/mock`), NOT here.
 *
 * - `Vsm` extends `ModuleEntityBase`, so `id`, `tenantId`, `creator`, `owner`
 *   (all `UserRef` / ISO-8601), `createdAt`, `updatedAt` come from the base.
 *   The oracle's flat `owner: string`, numeric epoch timestamps, slug ids, and
 *   catalog-only `linkedRefId` are dropped; the feature mock layer's seed
 *   adapter synthesises the base fields.
 * - Owners on user-denoting workshop fields (`Opportunity.owner`,
 *   `ImplementationPlanItem.owner`) are the shared `UserRef {id,name}`. The
 *   monitoring-projection owners (`ProcessNode.owner`, `Kaizen.owner`, …) stay
 *   plain `string` — they are denormalised display labels, not user references.
 * - Oracle → target renames: `KaizenIdea` → `Opportunity` (avoids clashing with
 *   the monitoring `Kaizen`/`KaizenAttachedWidget` types); `ImplementationItem`
 *   → `ImplementationPlanItem`.
 * - Status→tone/colour/label maps, quadrant helpers, canvas geometry, and seed
 *   fixtures are PRESENTATION / feature concerns owned elsewhere — by design
 *   they are not modelled here (this package stays runtime-dep-free, ADR-0012).
 */
// --- Enumerations -----------------------------------------------------------
/** Which state of the value stream a map represents. */
export const VSM_STATES = ["current", "future", "ideal"];
/** Lifecycle status of a VSM. */
export const VSM_STATUSES = ["Draft", "Active", "Archived"];
/**
 * The eight lean wastes (TIMWOODS). The oracle carries the classic seven
 * (TIMWOOD); `skills` (non-utilised talent) is the eighth, added here as the
 * forward contract.
 */
export const WASTE_TYPES = [
    "transport",
    "inventory",
    "motion",
    "waiting",
    "overproduction",
    "overprocessing",
    "defects",
    "skills",
];
/** The six workshop phases, in order (oracle `VsmPhaseId`, `phases.ts`).
 *  Kaizen was retired — see ADR-0042. */
export const VSM_PHASES = [
    "preparation",
    "sipoc",
    "current-vsm",
    "impact-effort",
    "future-vsm",
    "implementation",
];
/** Stable artifact-body identities shared by VSM clients and services. */
export const VSM_ARTIFACT_KEYS = [
    "charter",
    "project-context",
    "sipoc",
    "impact-effort",
    "control-plan",
    "implementation-plan",
    "workshop-notes",
    "current-state-build",
];
export const VSM_ARTIFACT_KEY = {
    CHARTER: "charter",
    PROJECT_CONTEXT: "project-context",
    SIPOC: "sipoc",
    IMPACT_EFFORT: "impact-effort",
    CONTROL_PLAN: "control-plan",
    IMPLEMENTATION_PLAN: "implementation-plan",
    WORKSHOP_NOTES: "workshop-notes",
    CURRENT_STATE_BUILD: "current-state-build",
};
/** Retired persisted phase ids mapped to their current workshop phase. */
export const LEGACY_PHASE_ALIAS = {
    kaizen: "impact-effort",
};
/** Completion state of a workshop phase / activity. */
export const VSM_PHASE_STATUSES = [
    "not-started",
    "in-progress",
    "complete",
];
/**
 * Edit-side connection (arrow) type between placed symbols. Electronic vs manual
 * information flow is conveyed by placed glyphs, not by widening this — kept
 * narrow deliberately. Distinct from the monitoring `EdgeKind`.
 */
export const VSM_CONNECTION_TYPES = ["push", "info", "pull"];
/** What map entity an opportunity (kaizen idea) is anchored to. */
export const OPPORTUNITY_LINK_TYPES = [
    "process-step",
    "edge",
    "buffer",
    "rework-loop",
    "control",
];
/**
 * @deprecated Use `VSM_IMPROVEMENT_STATUSES`; removal follows with the legacy
 * Opportunity interface in Wave 3.
 */
export const OPPORTUNITY_STATUSES = [
    "idea",
    "selected",
    "converted-to-kaizen",
];
/**
 * Optional coarse classification of an opportunity's delivery vehicle. Not in
 * the oracle — a forward addition (JDI = Just Do It).
 */
export const OPPORTUNITY_CLASSIFICATIONS = [
    "JDI",
    "Kaizen",
    "Project",
];
/** Delivery status of an implementation-plan item. */
export const IMPLEMENTATION_STATUSES = [
    "to-do",
    "on-track",
    "at-risk",
    "blocked",
    "validating-impact",
    "completed",
];
// --- Monitoring (ported from vsmMonitoring.ts) ------------------------------
// The live value-stream monitoring view. Presentation maps (status→tone/label),
// lane geometry, ladder/format helpers, and seed streams stay in the feature
// layer. Node/edge/kaizen/insight owners are denormalised display strings, not
// UserRefs. `EdgeKind` (material|info|rework) is a SEPARATE vocabulary from the
// edit-side `VsmConnectionType` (push|info|pull) — do not conflate them.
/** Swimlane a monitoring node sits in. */
export const LANE_IDS = [
    "customer",
    "planning",
    "supplier",
    "production",
    "quality",
];
/** Health status of a monitored process/quality node. */
export const PROCESS_STATUSES = [
    "healthy",
    "improving",
    "at-risk",
    "blocked",
    "bottleneck",
];
/** Monitoring edge (flow) kind. Distinct from the edit-side `VsmConnectionType`. */
export const EDGE_KINDS = ["material", "info", "rework"];
/**
 * The four execution states of an **Implementation Plan** row — the Kaizen
 * Newspaper's `status` column, set when a Kaizen is promoted and edited there
 * afterwards.
 *
 * NOT deprecated, and deliberately NOT `VSM_IMPROVEMENT_STATUSES`. These are the
 * two halves of the two-artifact model: a Kaizen burst is an *opportunity* and
 * carries no lifecycle of its own, while the Implementation Plan tracks the work
 * it became. The old `@deprecated Use VSM_IMPROVEMENT_STATUSES` note pointed at
 * the shape-side vocabulary that is being retired from KAIZEN, which is exactly
 * backwards.
 *
 * `VSM_IMPROVEMENT_STATUSES` survives for `QUALITY_PROBLEM`, which still carries
 * a `status` field.
 */
export const VSM_PLAN_STATUSES = [
    "on-track",
    "at-risk",
    "blocked",
    "done",
];
/** Tone of an AI insight callout. */
export const AI_INSIGHT_TONES = ["warning", "info", "alert"];
/** Kind of widget attached to a monitoring node. */
export const ATTACHED_WIDGET_KINDS = [
    "kaizen",
    "bottleneck",
    "wip-alert",
    "quality-loss",
    "control-health",
];
/**
 * Reader-facing names for the VSM workshop artifact cards (PB-5166).
 *
 * These live HERE, not in `packages/vsm-feature`, because the audit phrase
 * layer in `@playbookai/api-clients` needs them and a non-feature package may
 * not import a `*-feature` sibling (ADR-0009). Same reasoning that puts
 * `VSM_SHAPE_CATALOGUE` in this package.
 *
 * Every value is AUTHORED IN CODE — none of it is user text — so it is safe to
 * resolve into an audit row's copy. The card's own contents are user data and
 * never reach an audit payload; only the id does.
 *
 * The feature package's `WORKSHOP_ARTIFACTS` catalogue holds the same titles
 * for the canvas chrome. A drift guard in `packages/vsm-feature` pins the two
 * together, so the History rail can never name a card differently from the
 * card itself — the trap `widget-labels.test.ts` already exists to catch for
 * widgets.
 */
export const VSM_ARTIFACT_LABELS = {
    "artifact-charter": "Workshop Charter",
    "artifact-preparation": "Preparation",
    "artifact-project-context": "Project Context",
    "artifact-sipoc": "SIPOC",
    "artifact-impact-effort": "Impact vs Effort",
    "artifact-plan": "Implementation Plan",
    "artifact-control": "Control Plan",
    "artifact-notes": "Workshop Notes",
};
/**
 * Reader-facing names for the two VSM arrow flow kinds (PB-5168).
 *
 * Here for the same reason as {@link VSM_ARTIFACT_LABELS}: the audit phrase
 * layer in `@playbookai/api-clients` needs them and may not import a
 * `*-feature` package (ADR-0009).
 *
 * Only the flow KIND is ever resolved. `VsmMaterialFlowDetails` carries an
 * optional free-text `label` beside `flowKind`, and that is user content — it
 * must never reach an audit payload or a rendered row.
 */
export const VSM_FLOW_LABELS = {
    MATERIAL: "material flow",
    INFORMATION: "information flow",
};
//# sourceMappingURL=vsm.js.map